import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useColorScheme } from "@mui/joy/styles";

import { useAxios, useApiResponseParser } from "./";
import {
    type AccentColor,
    findAccentByName,
    resolveAccentBackground,
    resolveAccentForeground,
    resolveEventAccent,
} from "../utils/accentColors";

export const ACCENT_COLORS_QUERY_KEY = ["accent-colors"] as const;

export default function useAccentColors() {
    const { axiosPrivate } = useAxios();
    const { parseSuccessResponseData } = useApiResponseParser();
    const { mode } = useColorScheme();

    const query = useQuery({
        queryKey: ACCENT_COLORS_QUERY_KEY,
        queryFn: () =>
            axiosPrivate
                .get(`/v1/colors/list`)
                .then(parseSuccessResponseData) as Promise<AccentColor[]>,
        staleTime: 60_000,
    });

    const colors = useMemo(
        () => (Array.isArray(query.data) ? query.data : []),
        [query.data],
    );

    const getBackground = (name: string | null | undefined) =>
        resolveAccentBackground(findAccentByName(colors, name), mode);

    const getForeground = (name: string | null | undefined) =>
        resolveAccentForeground(findAccentByName(colors, name), mode);

    const resolve = (name: string | null | undefined) =>
        resolveEventAccent(colors, name, mode);

    return {
        colors,
        mode,
        isLoading: query.isLoading,
        isError: query.isError,
        refetch: query.refetch,
        getBackground,
        getForeground,
        resolve,
        findByName: (name: string | null | undefined) =>
            findAccentByName(colors, name),
    };
}
